import axiosInstance from "@/services/api";
import { useInvalidate, useTranslate } from "@refinedev/core";
import { notification } from "antd";
import { useCustomInvalidate } from "hooks/useCustomInvalidate/";
import { useWsSubscription } from "hooks/useWsSubscription";
import { useCallback, useEffect, useRef, useState } from "react";
import { NOTIFICATIONS_EVENT_TYPE, WSEvent } from "store/websocketStore";
import { getQueryKey } from "../components/ContactWithPresenter/hooks/useInfiniteVoiceFaceProfiles";
import { ActiveTabName } from "../components/ContactWithPresenter/types";

export interface UseVoicePreviewGenerationOptions {
  voiceProfileId: string | undefined;
  organizationId: string | undefined;
  /**
   * Current preview URL for the voice/language combo. When this transitions
   * from falsy to truthy while a request is pending, the hook treats the
   * request as resolved (covers the polling-fallback case where the cache
   * delivered the path before any WS event).
   */
  currentPreviewPath?: string | null;
  /** Called when generation finishes successfully — useful for autoplay. */
  onSuccess?: (language: string) => void;
}

const POLL_INTERVAL_MS = 5000;
const MAX_WAIT_MS = 60000;

/**
 * Triggers on-demand preview audio generation for a voice profile language.
 *
 * Resolution path: the BE emits a `VoiceProfileStatusChanged` event with
 * `operation: "PreviewGeneration"` over WebSocket, routed by `voice_profile_id`.
 *
 * Polling fallback: WS delivery has been observed to fail in some envs (idle
 * disconnect, missed event after reconnect). After firing the request we also
 * refresh the caches that expose this voice profile every
 * {@link POLL_INTERVAL_MS} for up to {@link MAX_WAIT_MS}. Whichever path
 * resolves first clears the pending state.
 */
export const useVoicePreviewGeneration = ({
  voiceProfileId,
  organizationId,
  currentPreviewPath,
  onSuccess,
}: UseVoicePreviewGenerationOptions) => {
  const t = useTranslate();
  const { customInvalidate } = useCustomInvalidate();
  const invalidate = useInvalidate();
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Refresh every cache that exposes this voice profile so the new preview
  // path propagates wherever the voice is rendered:
  //   - the premade voice list (voice picker on contact creation)
  //   - the contacts resource (AvatarPreviewCard embeds voice in contact data)
  // invalidateQueries is a no-op for caches with no active observers, so
  // unconditionally invalidating both is safe and avoids prop-drilling a
  // resource hint through SliderVoicePreview.
  const invalidateAffectedCaches = useCallback(() => {
    if (!organizationId) return;
    customInvalidate(
      getQueryKey("voice", ActiveTabName.Premade, organizationId)
    ).catch((error) => {
      console.error(
        "[useVoicePreviewGeneration] error invalidating voice cache",
        error
      );
    });
    invalidate({
      resource: `media/${organizationId}/contacts`,
      invalidates: ["all"],
    }).catch((error) => {
      console.error(
        "[useVoicePreviewGeneration] error invalidating contacts cache",
        error
      );
    });
  }, [customInvalidate, invalidate, organizationId]);

  const handleEvent = useCallback(
    (event: WSEvent) => {
      if (
        !voiceProfileId ||
        !pendingLanguage ||
        event.event !== NOTIFICATIONS_EVENT_TYPE.VoiceProfileStatusChanged ||
        event.operation !== "PreviewGeneration" ||
        event.voice_profile_id !== voiceProfileId
      ) {
        return;
      }
      if (event.status === "Success") {
        // Don't clear pending here — let the currentPreviewPath effect handle
        // it once the cache actually delivers the URL. That keeps onSuccess
        // (autoplay) tied to the moment the audio is renderable.
        invalidateAffectedCaches();
      } else if (event.status === "Failed") {
        stopPolling();
        notification.error({
          message: t(
            "media.components.VoiceProfileCreateForm.previewGenerationFailed"
          ),
          description: t(
            "media.components.VoiceProfileCreateForm.previewGenerationFailedDescription"
          ),
        });
        setPendingLanguage(null);
      }
    },
    [voiceProfileId, pendingLanguage, stopPolling, invalidateAffectedCaches, t]
  );

  // Only open a subscription while a request is in flight, so we don't have
  // one WS subscription per voice card on idle.
  useWsSubscription(
    {
      type: "voice_profile",
      id: pendingLanguage ? voiceProfileId : undefined,
    },
    handleEvent
  );

  // Cleanup timers on unmount.
  useEffect(() => stopPolling, [stopPolling]);

  // When the cache delivers the preview path (via WS invalidation OR polling),
  // wind down: stop polling, clear pending state, fire onSuccess for autoplay.
  useEffect(() => {
    if (!pendingLanguage || !currentPreviewPath) return;
    stopPolling();
    onSuccess?.(pendingLanguage);
    setPendingLanguage(null);
  }, [currentPreviewPath, pendingLanguage, stopPolling, onSuccess]);

  const requestPreview = useCallback(
    async (language: string) => {
      if (!voiceProfileId || !organizationId) return;
      if (pendingLanguage) return;
      setPendingLanguage(language);
      try {
        await axiosInstance.post(
          `/media/voice_profiles/${voiceProfileId}/generate_preview`,
          { languages: [language] },
          { params: { organization_id: organizationId } }
        );
        // Polling fallback: refetch the list periodically. If the new path
        // appears, the card transitions out of `isPending` because the
        // VoiceProfileResponse arrives with a non-null preview_asset_path.
        // We don't know which event won, so we always clear pending after the
        // hard timeout.
        pollTimerRef.current = setInterval(
          invalidateAffectedCaches,
          POLL_INTERVAL_MS
        );
        timeoutRef.current = setTimeout(() => {
          stopPolling();
          setPendingLanguage(null);
          notification.error({
            message: t(
              "media.components.VoiceProfileCreateForm.previewGenerationFailed"
            ),
            description: t(
              "media.components.VoiceProfileCreateForm.previewGenerationFailedDescription"
            ),
          });
        }, MAX_WAIT_MS);
      } catch (error) {
        console.error(
          "[useVoicePreviewGeneration] generate_preview request failed",
          error
        );
        setPendingLanguage(null);
        notification.error({
          message: t(
            "media.components.VoiceProfileCreateForm.previewGenerationFailed"
          ),
          description: t(
            "media.components.VoiceProfileCreateForm.previewGenerationFailedDescription"
          ),
        });
      }
    },
    [
      voiceProfileId,
      organizationId,
      pendingLanguage,
      invalidateAffectedCaches,
      stopPolling,
      t,
    ]
  );

  return {
    requestPreview,
    isPending: pendingLanguage !== null,
    pendingLanguage,
  };
};
